import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setUnsavedChanges: (dirty: boolean) => void;
  confirmNavigation: (onConfirm: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType>({
  hasUnsavedChanges: false,
  setUnsavedChanges: () => {},
  confirmNavigation: (onConfirm) => onConfirm(),
});

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dirty, setDirty] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  const setUnsavedChanges = useCallback((value: boolean) => {
    setDirty(value);
  }, []);

  const confirmNavigation = useCallback((onConfirm: () => void) => {
    if (!dirty) {
      onConfirm();
      return;
    }
    pendingNavigationRef.current = onConfirm;
    setDialogOpen(true);
  }, [dirty]);

  const handleDiscard = () => {
    setDialogOpen(false);
    setDirty(false);
    pendingNavigationRef.current?.();
    pendingNavigationRef.current = null;
  };

  const handleStay = () => {
    setDialogOpen(false);
    pendingNavigationRef.current = null;
  };

  return (
    <UnsavedChangesContext.Provider value={{ hasUnsavedChanges: dirty, setUnsavedChanges, confirmNavigation }}>
      {children}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) handleStay(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes that will be lost if you navigate away.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleStay}>Stay on Page</Button>
            <Button variant="destructive" onClick={handleDiscard}>Discard Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
};
